import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { Schedule, ScheduleCreate, ScheduleFire, ScheduleUpdate } from "@/types";

const schedulesKey = ["personal-agent", "schedules"] as const;
const firesKey = (scheduleId: string) => ["personal-agent", "schedule-fires", scheduleId] as const;

export function useSchedules() {
  return useQuery<Schedule[]>({
    queryKey: schedulesKey,
    queryFn: async () => {
      const res = await apiClient.get<Schedule[]>("/personal-agent/schedules");
      return res.data;
    },
  });
}

export function useScheduleFires(scheduleId: string | undefined, limit = 10) {
  return useQuery<ScheduleFire[]>({
    queryKey: scheduleId ? firesKey(scheduleId) : ["personal-agent", "schedule-fires", "none"],
    queryFn: async () => {
      const res = await apiClient.get<ScheduleFire[]>(
        `/personal-agent/schedules/${scheduleId}/fires?limit=${limit}`,
      );
      return res.data;
    },
    enabled: !!scheduleId,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ScheduleCreate) => {
      const res = await apiClient.post<Schedule>("/personal-agent/schedules", body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesKey }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ScheduleUpdate }) => {
      const res = await apiClient.patch<Schedule>(`/personal-agent/schedules/${id}`, body);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: schedulesKey });
      qc.invalidateQueries({ queryKey: firesKey(vars.id) });
    },
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/personal-agent/schedules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesKey }),
  });
}
